import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useSiteContent } from "../context/SiteContentContext";
import { GraduationCap, Award, ShieldCheck, Stethoscope, BookOpen, Trophy, Languages } from "lucide-react";

export default function About() {
  const navigate = useNavigate();
  const { about, clinic } = useSiteContent();
  return (
    <div data-testid="about-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div className="fade-up">
          <span className="inline-block bg-[#E8DAB2]/60 text-slate-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            About the Doctor
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 leading-tight">
            Dr. Prathusha K
          </h1>
          <p className="text-[#5E8B7E] font-medium mt-2">{clinic.title} · Banjara Hills, Hyderabad</p>
          <p className="mt-6 text-slate-600 leading-relaxed">{about.p1}</p>
          <p className="mt-4 text-slate-600 leading-relaxed">{about.p2}</p>
          <p className="mt-4 text-slate-600 leading-relaxed">{about.p3}</p>

          <div className="mt-8 space-y-4">
            {[
              { icon: GraduationCap, title: "M.B.B.S. · M.S. (Obs & Gyn)", text: "M.B.B.S. from SDUAHER, Kolar · Master of Surgery in Obstetrics & Gynaecology from J.J.M. Medical College, Davangere" },
              { icon: Award, title: "Fellowship in Endogynecology — KNRUHS", text: "Advanced fellowship at CARE Hospitals, Hyderabad (KNRUHS) in minimally invasive endogynecological surgery, mentored by Padma Shri Dr. Manjula Anagani" },
              { icon: Stethoscope, title: "Consultant — CARE Hospitals, Hyderabad", text: "Currently Consultant Obstetrician & Gynaecologist; previously consultant at Arete Hospital and Sai Shourya Hospital, Hyderabad" },
              { icon: BookOpen, title: "Healthcare Management — ISB", text: "Diploma in Healthcare Management from the Indian School of Business (2025), blending clinical excellence with better systems of care" },
            ].map((c) => (
              <div key={c.title} className="flex gap-4 bg-white rounded-2xl border border-[#EDF1EF] p-5">
                <span className="w-11 h-11 rounded-xl bg-[#D9E4E1] flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-[#4A7065]" />
                </span>
                <div>
                  <h3 className="font-heading font-semibold text-slate-800">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="fade-up" style={{ animationDelay: "0.15s" }}>
          <img
            src="/images/dr-prathusha.jpg"
            alt="Dr. Prathusha Kolachana — Endogynecologist, Banjara Hills, Hyderabad"
            className="rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] w-full object-cover aspect-[4/4] object-top"
            data-testid="about-doctor-photo"
          />
          <div className="bg-[#2C557E] rounded-2xl p-7 mt-6 text-white">
            <h2 className="font-heading text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#E8DAB2]" /> Our Clinic Philosophy
            </h2>
            <p className="mt-3 text-blue-100 text-sm leading-relaxed">
              <b className="text-white">The patient always comes first.</b> Every decision in this
              clinic — from how we schedule appointments to how we explain a diagnosis — is guided
              by your <b className="text-white">Patient Rights</b> and your{" "}
              <b className="text-white">Safety</b>:
            </p>
            <ul className="mt-4 space-y-2 text-sm text-blue-100 list-disc list-inside">
              <li>The right to be heard fully, without judgement or hurry</li>
              <li>The right to clear, honest information about every option</li>
              <li>The right to privacy — your records belong to you</li>
              <li>The right to consent — nothing happens without your informed yes</li>
              <li>Safety protocols in every procedure, every prescription, every time</li>
            </ul>
          </div>
          <Button
            data-testid="about-book-btn"
            onClick={() => navigate("/booking")}
            className="mt-6 w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6 text-base"
          >
            Book an Appointment with Dr. Prathusha
          </Button>
        </div>
      </div>

      {/* Expertise, Recognition & Languages */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="about-expertise-section">
        <div className="bg-white rounded-2xl border border-[#EDF1EF] p-6">
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-[#5E8B7E]" /> Areas of Expertise
          </h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              "Laparoscopic gynaecological surgery",
              "Robotic-assisted surgery",
              "High-risk obstetrics & deliveries",
              "Hysterectomy & myomectomy",
              "Ovarian cystectomy",
              "Ectopic pregnancy management",
              "Aesthetic gynecology",
              "PCOS & fertility care",
            ].map((e) => (
              <span key={e} className="text-xs bg-[#D9E4E1]/50 text-slate-700 rounded-full px-3 py-1.5">
                {e}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#EDF1EF] p-6">
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#5E8B7E]" /> Recognition & Academics
          </h3>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
            <li>• 2nd prize, FOGSI national poster presentation on "Cervical Cancer Elimination" (2021)</li>
            <li>• Published research on labour augmentation in the JEMDS journal</li>
            <li>• Presenter at FIGO–FOGSI International Conference on Women's Health, HICC Hyderabad (2023)</li>
            <li>• Presenter at OGSH Annual State Conference — Yukti 2025</li>
          </ul>
        </div>
        <div className="bg-white rounded-2xl border border-[#EDF1EF] p-6">
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
            <Languages className="w-5 h-5 text-[#5E8B7E]" /> Languages Spoken
          </h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {["English", "Telugu", "Hindi", "Kannada"].map((l) => (
              <span key={l} className="text-sm bg-[#E8DAB2]/50 text-slate-700 rounded-full px-4 py-1.5">
                {l}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed">
            Consult comfortably in the language you think in — because the best care begins with
            being truly understood.
          </p>
        </div>
      </div>
    </div>
  );
}
